import React, { useState, useEffect } from "react";
import { Box, TextField, IconButton, InputAdornment } from "@mui/material";
import ImageIcon from "assets/icons/ImageIcon";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "react-i18next";
import { SharePopup, useSharePopup } from "components/SharePopup";
import { useAppStore, useErrorAppStore } from "store/appStore";
import showPreviewIcon from "utils/showPreviewImage";
import ImportPwa from "assets/icons/ImportPwa";
import Template from "assets/icons/Template";
import PlayIcon from "assets/icons/PlayIcon";
import StopIcon from "assets/icons/StopIcon";
import useFetch from "hooks/useFetch";
import useIdStore from "store/pwaId";
import CustomButton from "components/Button";
import useFetchFormData from "hooks/useFetchFormData";
import CautionModal from "components/CautionModal";
import useCautionStore from "store/cautionStore";
import { useNavigate, useParams } from "react-router-dom";
import { getToken } from "utils/getToken";
import ToastMessage from "components/ToastMessage";
import { BootstrapTooltip } from "./CommentsTable";
import usePwaSave from "hooks/usePwaSave";
import EditIcon from "@mui/icons-material/Edit";
import useMediaByValue from "hooks/useMediaByValue";

export const buttonsStyles = {
  width: 40,
  height: 40,
  borderRadius: "8px",
  background: "#fff",
  border: "1px solid #C9D0D8",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",

  "& svg": { width: "20px", height: "20px" },
};

export default function NameApp() {
  const { appData, saveAppData } = useAppStore();
  const { errors, clearError } = useErrorAppStore();
  const isTablet = useMediaByValue("(min-width:768px)");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [image, setImage] = useState<string | File | null>(null);
  const [showDeleteIcon, setShowDeleteIcon] = useState(true);
  const { id: paramsID } = useParams();
  const { id = paramsID, setId } = useIdStore();
  const { isOpen } = useCautionStore();
  const session = getToken();
  const { isLoading, handlePwa } = usePwaSave();

  useEffect(() => {
    paramsID && setId(paramsID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsID]);

  const { triggerFetch: updatePWA } = useFetchFormData({
    url: `pwa/${paramsID}`,
    method: "PUT",
    headers: {
      Authorization: `Bearer ${session}`,
    },
  });

  const { openPopup, closePopup, shareLink, copied, setCopied, handleCopy, handleOpenPopup } = useSharePopup();
  const { triggerFetch: triggerShare } = useFetch({
    url: `pwa/${paramsID}/share-pwa`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${session}`,
    },
  });

  const handleShare = async () => {
    if (id) {
      try {
        const shareLink = await triggerShare();
        handleOpenPopup(window.location.host + shareLink?.data?.shareLink);
      } catch (error) {
        console.error("Ошибка при получении ссылки для шаринга:", error);
      }
    }
  };

  useEffect(() => {
    if (appData.creatives?.icon) {
      setImage(appData.creatives.icon);
    }
  }, [appData.creatives?.icon]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setImage(file);
        saveAppData({ creatives: { ...appData.creatives, icon: file } });
      } else {
        alert("Select image!");
      }
    }
  };

  const handleAppNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    saveAppData({ appName: e.target.value });
    if (e.target.value) {
      clearError("appName");
    }
  };
  const token = getToken();
  const { triggerFetch: triggerCopyPwa } = useFetch({
    url: `pwa/copy/${paramsID}`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  async function handleCopyPwa() {
    const res = await triggerCopyPwa();

    if (res.success) {
      ToastMessage({ mode: "info", message: t("succescopy") });
      setTimeout(() => {
        navigate("/pwa");
      }, 3000);
    }
  }

  const handleImageRemove = () => {
    setImage(null);
    saveAppData({ creatives: { ...appData.creatives, icon: null } });
  };

  const handlePWAStatusChange = (status: "active" | "pause") => {
    if (!appData?._id) {
      ToastMessage({ mode: "error", message: t("PWAStatusChangeError") });
      return;
    }
    saveAppData({ status }, false);
    const data = new FormData();
    data.append("status", status);
    void updatePWA(data);
    ToastMessage({ mode: "info", message: t(status === "active" ? "pwaActivated" : "pwaPaused") });
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          marginBottom: { xs: "16px", md: "24px" },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", paddingBottom: "24px" }}>
          <Box
            sx={{
              marginRight: { xs: "12px", md: "16px" },
              position: "relative",
              "&:hover .delete-icon": {
                opacity: 1,
              },
            }}
            onMouseEnter={() => setShowDeleteIcon(true)}
            onMouseLeave={() => setShowDeleteIcon(false)}
          >
            {image ? (
              <>
                {showDeleteIcon && (
                  <IconButton
                    className="delete-icon"
                    sx={{
                      position: "absolute",
                      top: "-10px",
                      right: "-10px",
                      zIndex: 1,
                      color: "white",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                    }}
                    onClick={handleImageRemove}
                  >
                    <CancelIcon color="warning" fontSize="small" />
                  </IconButton>
                )}
                <img
                  src={showPreviewIcon(image)}
                  alt="select-image"
                  className="uploaded-image"
                  style={{
                    width: isTablet ? 88 : 64,
                    height: isTablet ? 88 : 64,
                    borderRadius: isTablet ? "16px" : "12px",
                  }}
                />
              </>
            ) : (
              <label className="custom-input">
                <input
                  id="fileInput"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
                <IconButton
                  sx={{
                    background: "#F6F7FB",
                    width: isTablet ? 88 : 64,
                    height: isTablet ? 88 : 64,
                    borderRadius: isTablet ? "16px" : "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyItems: "center",

                    "& svg": { width: { xs: "18px", md: "24px" }, height: { xs: "18px", md: "24px" } },
                  }}
                  component="span"
                >
                  {appData.creatives.icon ? (
                    <img
                      src={showPreviewIcon(appData.creatives.icon)}
                      alt="selected-image"
                      style={{
                        width: isTablet ? 88 : 64,
                        height: isTablet ? 88 : 64,
                        borderRadius: isTablet ? "16px" : "12px",
                      }}
                    />
                  ) : (
                    <ImageIcon />
                  )}
                </IconButton>
              </label>
            )}
          </Box>
          <Box>
            <TextField
              required
              sx={{
                input: {
                  padding: 0,
                  margin: 0,
                  fontFamily: "var(--cygreRegular), sans-serif",
                  fontSize: { xs: "20px", md: "24px" },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: "#2e263d",

                  "&::placeholder": {
                    fontFamily: "var(--cygreRegular), sans-serif",
                    fontSize: { xs: "20px", md: "24px" },
                    fontWeight: 400,
                    lineHeight: 1.6,
                    textOverflow: "ellipsis !important",
                    color: "#6d6777",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              fullWidth
              error={!!errors.appName}
              helperText={errors.appName}
              placeholder={t("nameApp")}
              value={appData.appName}
              onChange={handleAppNameChange}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <EditIcon
                        sx={{
                          width: { xs: "16px", md: "20px" },
                          height: { xs: "16px", md: "20px" },
                          color: "#9d9d9d",
                        }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <BootstrapTooltip title={t("sharepwa")} placement="top">
              <Box component="button" onClick={handleShare} sx={{ ...buttonsStyles }}>
                <ImportPwa color={""} />
              </Box>
            </BootstrapTooltip>
            <BootstrapTooltip title={t("copy")} placement="top">
              <Box
                onClick={handleCopyPwa}
                component="button"
                sx={{ ...buttonsStyles, "& svg": { width: "24px", height: "24px" } }}
              >
                <Template color={""} />
              </Box>
            </BootstrapTooltip>
            {appData.status === "pause" && (
              <BootstrapTooltip title={t("setactive")} placement="top">
                <Box component="button" onClick={() => handlePWAStatusChange("active")} sx={{ ...buttonsStyles }}>
                  <PlayIcon />
                </Box>
              </BootstrapTooltip>
            )}
            {appData.status === "active" && (
              <BootstrapTooltip title={t("setstop")} placement="top">
                <Box onClick={() => handlePWAStatusChange("pause")} component="button" sx={{ ...buttonsStyles }}>
                  <StopIcon />
                </Box>
              </BootstrapTooltip>
            )}
          </Box>
          <Box
            sx={{
              marginTop: "16px",
            }}
          >
            <CustomButton
              textButton={t("save")}
              height={40}
              width={"100%"}
              customBgColor="#F4701B"
              customHoverColor="#F4701B"
              customBorderRadius="8px"
              customFontSize="14px"
              loading={isLoading}
              onClick={handlePwa}
            />
          </Box>
        </Box>
      </Box>
      <SharePopup
        open={openPopup}
        onClose={closePopup}
        shareLink={shareLink}
        copied={copied}
        setCopied={setCopied}
        handleCopy={handleCopy}
      />
      {isOpen && <CautionModal handleConfirm={handlePwa} isLoading={isLoading} />}
    </>
  );
}
